/**
 * Whether a `text-…` value names one of this workspace's typography sizes rather than a colour.
 *
 * `text-*` is ambiguous — `text-body-bold-12` is a font size, `text-gray-dark` is a colour — and
 * tailwind-merge only knows the stock scale, so it reads every unfamiliar `text-…` as a colour.
 * Both then land in the same class group and it drops one, silently. `cn` uses this to tell them
 * apart; see `cn.ts`.
 *
 * Matched by shape rather than by a list of all 34 names: the families are closed and each is
 * suffixed with a pixel value, so a size added to `fontSize` in the Tailwind preset is understood
 * without this file being edited. No theme colour has that shape, which is what makes the shape
 * safe to match on -- `typography-scale.test.mjs` holds both halves of that.
 *
 * Dependency-free on purpose: the test step runs without `npm install`, so the part worth testing
 * lives here rather than in `cn.ts`, which imports tailwind-merge.
 *
 * @see packages/ui/src/tailwind/themes.ts — the `fontSize` block this mirrors
 */
export const TYPOGRAPHY_SCALE_PATTERN = /^(?:body-bold|input-labels|headline|overline|button|body)-\d+$/;

/** The bare numeric sizes on the same scale: `text-5`, `text-7`, `text-8`, `text-9`. */
const BARE_NUMERIC = /^\d+$/;

export function isTypographyScaleClass(value: string): boolean {
    return TYPOGRAPHY_SCALE_PATTERN.test(value) || BARE_NUMERIC.test(value);
}
